import { ServerConfig } from "@/config/config";
import { Job } from "@/models/job";
import { Axios } from "@/utils/axios";
import { IGetResponse } from "@/utils/get-response";
import { IPaginationData } from "@/utils/query-params";
import { ProductCSVImportProductMatchingMethod } from "./product-service";
import { Contact, Product } from "@/models/base";
import { VatService } from "./vat-service";
import { SupplierService } from "./supplier-service";
import { ExcelMapperService } from "@/utils/services/excel-mapper-service";
import { IPriceListProduct } from "@/utils/price-list/price-list-product";
import { HumanFilter } from "@/utils/human-filter";

export interface IExcelMapperExtraData {
	SupplierID?:number;
	ProductMatchingMethod?:ProductCSVImportProductMatchingMethod;
}

export interface IFetchPriceListProductOptions {
	supplierNr?:string;
}


class PriceListProductServiceClass {
	private readonly url:string = `${ServerConfig.host}/price-lists`;
	public readonly excelMapperService = new ExcelMapperService(`${this.url}/excel-mapper-settings`)

	public async fetch(searchQuery:string, paginationData:IPaginationData, opts:IFetchPriceListProductOptions = {}):Promise<IGetResponse<IPriceListProduct>>{
		let result = await Axios.get(`${this.url}`, {
			params: {
				searchQuery,
				limit: paginationData.limit,
				order: paginationData.orderBy,
				orderDirection: paginationData.orderDirection,
				offset: paginationData.offset,
				supplierNr: opts.supplierNr || ""
			}
		});
		let items:IPriceListProduct[] = (result.data as IGetResponse<IPriceListProduct>).data;
		let supplierNrs = items.map(i=>(i.Suppliers || []).map(s=>s.ContactNR)).flat(1);
		supplierNrs = supplierNrs.filter((nr, i)=>nr != "" && supplierNrs.indexOf(nr) == i);
		if (supplierNrs.length > 0){
			let suppliers = await SupplierService.getContacts({
				orderBy: "ID",
				offset: 0,
				orderDirection: "ASC",
				limit: supplierNrs.length,
				additionalPreloads: ["MainAddress"],
				filters: [
					new HumanFilter({Field: "this.ComputedFriendlyID", Operator: "in", Values: supplierNrs})
				],
			});
			for (let item of items){
				for (let supplier of item.Suppliers) {
					supplier.Contact = suppliers.data.find(s=>s.ComputedFriendlyID == supplier.ContactNR);
				}
			}
		}
		return result.data;
	}

	public async findBySku(sku:string):Promise<Product|null>{
		let result = await Axios.get(`${this.url}/find-by-sku`, {
			params: {
				sku
			}
		});
		return new Product(result.data) || null;
	}

	async importPriceListProductsCSV(file:File,useCommaSeperator:boolean, productMatchingMethod:ProductCSVImportProductMatchingMethod,
		supplier:Contact | null = null):Promise<Job> {
		let data = new FormData();
		data.append("file", file, file.name);
		data.append("use-comma-as-decimal-seperator", useCommaSeperator ? "true" : "false");
		data.append("product-matching-method", productMatchingMethod);
		if (supplier){
			data.append("supplier-nr", supplier.ComputedFriendlyID);
		}
		let result = await Axios.post(`${this.url}/import-csv`, data);
		return new Job(result.data);
	}

	async importPriceListProductsExcel(file:File, productMatchingMethod:ProductCSVImportProductMatchingMethod, supplier:Contact | null = null):Promise<Job> {
		let data = new FormData();
		data.append("file", file, file.name);
		data.append("product-matching-method", productMatchingMethod);
		if (supplier){
			data.append("supplier-nr", supplier.ComputedFriendlyID);
		}
		let result = await Axios.post(`${this.url}/import-excel`, data);
		return new Job(result.data);
	}

	async toProduct(priceListItem:any):Promise<Product> {
		console.log(priceListItem);
		let data = {...priceListItem, ID: 0};
		try{
			data.Vat = VatService.getVatByValue(priceListItem.Vat);
		}catch(err){
			data.Vat = VatService.getDefaultVat();
		}
		data.VatID = data.Vat.ID;
		for (let supplier of (data.Suppliers || [])) {
			if (!supplier.ContactNR) continue;
			try{
				let result = await SupplierService.getByComputedFriendlyId(supplier.ContactNR);
				supplier.ContactID = result.ID;
			}catch(err){

			}
		}
		let product = new Product(data);
		if (product.Suppliers.length != 0) {
			product.Suppliers[0].IsMainSupplier = true;
		}
		product.Active = true;
		product.PriceListProductID = priceListItem.ID;
		return product;
	}

	async deleteItems(itemIds:string[]):Promise<void>{
		await Axios.delete(`${this.url}`, {
			params: {
				ids: itemIds
			}
		});
	}

	async allowedCols():Promise<string[][]>{
		let result = await Axios.get(`${this.url}/allowed-cols`);
		return result.data || [];
	}
}

export const PriceListProductService = new PriceListProductServiceClass();